import React, { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { Button, Form, Input, Label, Text } from '@thefarmersfront/kitchen-mobile';

import {
  GUIDE_MESSAGES,
  HELPER_TEXT,
  INCORRECT_OR_ALREADY_USED_PASSWORD_ERROR_CODE,
  INVALID_PASS_TOKEN_ERROR_CODE,
  NOT_FOUND_PASS_TOKEN_ERROR_CODE,
  PASSWORD_LENGTH_LIMIT,
  PLACEHOLDER_TEXT,
  REQUIRED_PASS_TOKEN_ERROR_CODE,
} from '../../constants';
import { useCheckPw, useFormSubmitStatus } from '../../hooks';
import { useCheckPasswordStore, useUserInfoStore } from '../../store';
import { TypeCheckPwInputForm } from '../../types';
import { defaultErrorHandler, hasSequentialNumber, pwValidator } from '../../utils';

import PasswordInputIcon from '../Icons/PasswordInputIcon';
import PageLayout from '../PageLayout/PageLayout';

import styles from './CheckPasswordForm.module.scss';

const CheckPasswordForm = ({ onSuccess }: { onSuccess: (passToken: string) => void }): React.JSX.Element => {
  const navigate = useNavigate();

  const { control, formState, setFocus, handleSubmit, setError, clearErrors } = useForm<TypeCheckPwInputForm>({
    mode: 'onChange',
    defaultValues: {
      password: '',
    },
  });
  const [showPassword, setShowPassword] = useState(false);

  const loginId = useUserInfoStore((state) => state.loginId);

  const password = useWatch({ control, name: 'password' });
  const { isSubmittable, blurActiveElement } = useFormSubmitStatus({ formState, setFocus });
  const requestCheckPassword = useCheckPw();

  const setIsValidPassToken = useCheckPasswordStore((state) => state.actions.setIsValidPassToken);

  useEffect(() => {
    setIsValidPassToken(false);
  }, []);

  const handleCheckPassword = async (data: TypeCheckPwInputForm): Promise<void> => {
    await blurActiveElement();

    await requestCheckPassword({
      params: {
        username: loginId,
        password: data.password,
      },
      onSuccess: (response) => {
        onSuccess(response?.data?.passToken ?? '');
      },
      onError: (error) => {
        const { code, message } = error;
        if (INCORRECT_OR_ALREADY_USED_PASSWORD_ERROR_CODE === code) {
          setError('password', { type: 'incorrect', message: message });
          return;
        }

        defaultErrorHandler(error);

        if (
          [NOT_FOUND_PASS_TOKEN_ERROR_CODE, REQUIRED_PASS_TOKEN_ERROR_CODE, INVALID_PASS_TOKEN_ERROR_CODE].includes(
            code
          )
        ) {
          navigate(-1);
        }
      },
    });
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth' as ScrollBehavior });
      e.target.focus({ preventScroll: true });
    }, 200);
  };

  return (
    <Form className={styles['check-password-form']} onSubmit={handleSubmit(handleCheckPassword)}>
      <PageLayout>
        <PageLayout.Body>
          <div className={styles['content-box']}>
            <Text className={styles['message-description']} color="secondary">
              {GUIDE_MESSAGES.VERIFY_PASSWORD}
            </Text>
            <div className={styles['input-container']}>
              <div className={styles['input-box']}>
                <Label className={styles['label']} htmlFor="loginId" value="아이디" />
                <Input id="loginId" className={styles['input-loginId']} disabled value={loginId} />
              </div>
              <Controller
                name="password"
                control={control}
                rules={{
                  required: HELPER_TEXT.REQUIRED_PASSWORD,
                  validate: {
                    format: (value) => {
                      if (hasSequentialNumber(value)) {
                        return HELPER_TEXT.INFO_PASSWORD_SEQUENTIAL_NUMBER;
                      }
                      if (!pwValidator(value)) {
                        return HELPER_TEXT.INFO_PASSWORD_CONDITION;
                      }
                    },
                  },
                }}
                render={({ field, fieldState }) => (
                  <div className={styles['input-box']}>
                    <Label className={styles['label']} htmlFor="password" value="비밀번호" />
                    <Input
                      placeholder={PLACEHOLDER_TEXT.PASSWORD}
                      type={showPassword ? 'text' : 'password'}
                      status={fieldState.error ? 'error' : 'default'}
                      validationMessage={fieldState.error?.message}
                      maxLength={PASSWORD_LENGTH_LIMIT}
                      autoComplete="off"
                      rightIcon={
                        <PasswordInputIcon
                          isEmpty={!field.value}
                          showPassword={showPassword}
                          setShowPassword={setShowPassword}
                        />
                      }
                      onFocus={(e) => {
                        if (fieldState.error?.type === 'incorrect') {
                          clearErrors('password');
                        }
                        handleInputFocus(e);
                      }}
                      {...field}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </PageLayout.Body>
        <PageLayout.Footer>
          <Button variant="contained" disabled={!isSubmittable || password.length === 0} type="submit" full>
            확인
          </Button>
        </PageLayout.Footer>
      </PageLayout>
    </Form>
  );
};

export default CheckPasswordForm;
